import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function isPWA() {
  if (typeof window === 'undefined') return false;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  const isIOSPWA = window.navigator.standalone === true;
  return isStandalone || isIOSPWA;
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export function formatNumber(num) {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-IN').format(num);
}

export function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
}

export function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

/** Display timezone for user-facing acceptance timestamps (IST). */
export const APP_DISPLAY_TIMEZONE = 'Asia/Kolkata';

/**
 * Format agreement acceptance timestamps in Asia/Kolkata.
 * Expects an ISO instant (prefer with Z / offset). Preserves the acceptance instant.
 */
export function formatAcceptedAt(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: APP_DISPLAY_TIMEZONE,
  });
}

export const statusColors = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  under_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  suspended: 'bg-red-50 text-red-700 border-red-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  archived: 'bg-slate-100 text-slate-500 border-slate-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  needs_correction: 'bg-orange-50 text-orange-700 border-orange-200',
};

export const tierColors = {
  bronze: 'bg-amber-50 text-amber-900 border-amber-200',
  silver: 'bg-slate-100 text-slate-800 border-slate-300',
  gold: 'bg-amber-100 text-amber-950 border-amber-300',
  platinum: 'bg-blue-50 text-blue-900 border-blue-200',
};

export const categoryLabels = {
  sub_dsa: 'Sub-DSA',
  ca: 'Chartered Accountant',
  ca_firm: 'CA Firm',
  broker: 'Broker',
  builder: 'Builder',
  insurance_agent: 'Insurance Agent',
  wealth_advisor: 'Wealth Advisor',
  individual: 'Individual',
  corporate: 'Corporate',
  freelancer: 'Freelancer',
  other: 'Other'
};

export const partnerTypes = [
  { value: 'individual', label: 'Individual' },
  { value: 'entity', label: 'Business Entity' }
];

export const partnerCategories = [
  { value: 'sub_dsa', label: 'Sub-DSA' },
  { value: 'ca', label: 'Chartered Accountant' },
  { value: 'ca_firm', label: 'CA Firm' },
  { value: 'broker', label: 'Broker' },
  { value: 'builder', label: 'Builder' },
  { value: 'insurance_agent', label: 'Insurance Agent' },
  { value: 'wealth_advisor', label: 'Wealth Advisor' },
  { value: 'individual', label: 'Individual Referrer' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'other', label: 'Other' }
];

/** Individual partners cannot select Corporate category. */
export function categoriesForPartnerType(partnerType) {
  if (partnerType === 'individual') {
    return partnerCategories.filter(c => c.value !== 'corporate');
  }
  return partnerCategories;
}

/** Store/display website without protocol; UI shows a static https:// prefix. */
export function stripWebsiteProtocol(url) {
  if (!url) return '';
  return String(url).trim().replace(/^https?:\/\//i, '');
}

export function withHttps(hostOrUrl) {
  const host = stripWebsiteProtocol(hostOrUrl);
  if (!host) return '';
  return `https://${host}`;
}

export function formatPartnerCategory(partner) {
  if (!partner) return '';
  const label = categoryLabels[partner.category] || partner.category || '';
  if (partner.category === 'other' && partner.category_other) {
    return `${label} (${partner.category_other})`;
  }
  return label;
}

export const AGREEMENT_COPY_DOC_TYPE = 'agreement_copy';
export const AGREEMENT_COPY_LABEL = 'Agreement Copy';
export const AGREEMENT_COPY_DOC_TYPES = [AGREEMENT_COPY_DOC_TYPE, 'signed_agreement'];

export function isAgreementCopyDocument(documentType) {
  return AGREEMENT_COPY_DOC_TYPES.includes(documentType);
}

export const DOCUMENT_UPLOAD_DEFAULT = {
  extensions: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
  accept: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
  label: 'PDF, JPG, PNG, DOC, DOCX',
};

const IMAGE_OR_PDF_FORMATS = {
  extensions: ['.jpg', '.jpeg', '.png', '.pdf'],
  accept: '.jpg,.jpeg,.png,.pdf',
  label: 'JPG, PNG, and PDF',
};

export const DOCUMENT_UPLOAD_RULES = {
  pan_card: {
    ...IMAGE_OR_PDF_FORMATS,
    errorMessage: 'Unsupported file type. PAN Card accepts JPG, PNG, and PDF files only.',
    hint: 'Upload a clear, readable copy of your PAN Card.',
  },
  address_proof: {
    ...IMAGE_OR_PDF_FORMATS,
    errorMessage: 'Unsupported file type. Address Proof accepts JPG, PNG, and PDF files only.',
    hint: 'Electricity Bill, Telephone Bill, or Recent Taxpayer Bill.',
  },
  cancelled_cheque: {
    ...IMAGE_OR_PDF_FORMATS,
    errorMessage: 'Unsupported file type. Cancelled Cheque accepts JPG, PNG, and PDF files only.',
    hint: 'Upload a clear image or PDF of a cancelled cheque with account details visible.',
  },
  photo: {
    extensions: ['.jpg', '.jpeg', '.png'],
    accept: '.jpg,.jpeg,.png',
    label: 'JPG, JPEG, PNG',
    errorMessage: 'Business Portrait must be an image. Allowed: JPG, JPEG, PNG',
  },
};

export function getDocumentUploadRule(documentType) {
  return DOCUMENT_UPLOAD_RULES[documentType] || DOCUMENT_UPLOAD_DEFAULT;
}

export function getDocumentAcceptAttribute(documentType) {
  return getDocumentUploadRule(documentType).accept;
}

export function getDocumentAcceptedFormatsLabel(documentType) {
  return getDocumentUploadRule(documentType).label;
}

/** Extra guidance shown with KYC upload instructions. Null when the document has none. */
export function getDocumentUploadHint(documentType) {
  return getDocumentUploadRule(documentType).hint || null;
}

export function validateDocumentFileExtension(documentType, fileName) {
  const rule = getDocumentUploadRule(documentType);
  const ext = String(fileName || '').substring(String(fileName || '').lastIndexOf('.')).toLowerCase();
  if (!rule.extensions.includes(ext)) {
    return rule.errorMessage || `Unsupported file type "${ext}". Allowed: ${rule.label}`;
  }
  return null;
}

export const documentTypes = [
  { value: 'pan_card', label: 'PAN Card', required: true },
  { value: 'address_proof', label: 'Address Proof', required: true },
  // Placed near required KYC so partners see it without scrolling past other optionals
  { value: 'cancelled_cheque', label: 'Cancelled Cheque', required: false },
  { value: 'aadhaar', label: 'Aadhaar Card', required: false },
  { value: 'gst_certificate', label: 'GST Certificate', required: false },
  { value: 'bank_statement', label: 'Bank Statement', required: false },
  { value: 'registration_certificate', label: 'Company Registration Certificate', required: false },
  { value: 'photo', label: 'Business Portrait', required: false },
  { value: 'other', label: 'Other Document', required: false }
];

/** KYC documents shown on the Documents onboarding step (excludes Agreement Copy). */
export const kycDocumentTypes = documentTypes;

export function areRequiredKycDocumentsUploaded(documents = [], types = kycDocumentTypes) {
  const required = (types || []).filter((dt) => dt.required);
  return required.every((dt) =>
    (documents || []).some((d) => d.document_type === dt.value)
  );
}

export function getDocumentTypeLabel(documentType) {
  if (!documentType) return 'Document';
  if (isAgreementCopyDocument(documentType)) return AGREEMENT_COPY_LABEL;
  const match = documentTypes.find(dt => dt.value === documentType);
  if (match?.label) return match.label;
  return documentType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export const partnerTypeLabels = {
  individual: 'Individual',
  entity: 'Business Entity'
};

export const addressTypeLabels = {
  registered: 'Registered Address',
  office: 'Office Address',
  residential: 'Residential Address',
  branch: 'Branch Address'
};

export const constitutionTypeLabels = {
  pvt_ltd: 'Private Limited',
  llp: 'LLP',
  partnership: 'Partnership',
  proprietorship: 'Proprietorship',
  individual: 'Individual',
  public_ltd: 'Public Limited',
  trust: 'Trust',
  society: 'Society',
  huf: 'HUF'
};

export const noteTypeLabels = {
  internal: 'Internal',
  general: 'General',
  followup: 'Follow-up',
  escalation: 'Escalation',
  compliance: 'Compliance'
};
